// Playground — send a raw Telegram message through the backend's channel
// parser and inspect the extracted signal. Matches the desktop's "playground"
// detail tab in functional intent; compact single-form UX for web.
import { useState } from "react";
import { ApiError } from "../../client/http-client";

interface PlaygroundSectionProps {
	profileId: string;
}

const API_BASE =
	(import.meta.env.VITE_SIGNALLENS_API_BASE as string | undefined) ?? "";

function getJwt(): string | null {
	try {
		const raw = localStorage.getItem("signallens.jwt");
		return raw || null;
	} catch {
		return null;
	}
}

export const PlaygroundSection = ({ profileId }: PlaygroundSectionProps) => {
	const [message, setMessage] = useState("");
	const [replyMessage, setReplyMessage] = useState("");
	const [telegramChatId, setTelegramChatId] = useState("");
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const run = async () => {
		setRunning(true);
		setResult(null);
		setError(null);
		try {
			const token = getJwt();
			const r = await fetch(
				`${API_BASE}/api/v1/profiles/${encodeURIComponent(profileId)}/channel-parser/analyze`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						...(token ? { Authorization: `Bearer ${token}` } : {}),
					},
					body: JSON.stringify({
						telegramChatId: telegramChatId
							? Number(telegramChatId)
							: undefined,
						message,
						replyMessage: replyMessage || undefined,
					}),
				},
			);
			const text = await r.text();
			if (!r.ok) {
				throw new ApiError(r.status, text || `status ${r.status}`);
			}
			try {
				setResult(JSON.stringify(JSON.parse(text), null, 2));
			} catch {
				setResult(text);
			}
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setRunning(false);
		}
	};

	return (
		<section className="tg-surface rounded-2xl">
			<header className="border-b border-[color:var(--color-tg-line)] px-4 py-3">
				<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
					Playground
				</h3>
				<p className="tg-text-muted mt-0.5 text-[0.82rem] leading-5">
					Feed a raw Telegram message through the parser + LLM pipeline to
					preview what signal it would extract. Does not create a trade.
				</p>
			</header>

			<div className="grid gap-3 px-4 py-4">
				<label className="grid gap-1.5">
					<span className="tg-text-primary text-[0.86rem] font-semibold">
						Telegram chat id (optional)
					</span>
					<input
						className="tg-input tg-text-primary h-10 rounded-[14px] px-3.5 text-[0.9rem]"
						value={telegramChatId}
						onChange={(e) => setTelegramChatId(e.target.value)}
						placeholder="-1001234567890"
						inputMode="numeric"
						disabled={running}
					/>
				</label>

				<label className="grid gap-1.5">
					<span className="tg-text-primary text-[0.86rem] font-semibold">
						Message
					</span>
					<textarea
						className="tg-input tg-text-primary min-h-[120px] rounded-[14px] px-3.5 py-2 text-[0.9rem]"
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						placeholder="Paste the raw Telegram message text here"
						disabled={running}
					/>
				</label>

				<label className="grid gap-1.5">
					<span className="tg-text-primary text-[0.86rem] font-semibold">
						Reply-to message (optional)
					</span>
					<textarea
						className="tg-input tg-text-primary min-h-[80px] rounded-[14px] px-3.5 py-2 text-[0.9rem]"
						value={replyMessage}
						onChange={(e) => setReplyMessage(e.target.value)}
						placeholder="Context message this replies to, if any"
						disabled={running}
					/>
				</label>

				<div className="flex items-center justify-end">
					<button
						type="button"
						onClick={() => void run()}
						disabled={!message.trim() || running}
						className="tg-btn-primary inline-flex h-9 items-center justify-center rounded-xl px-4 text-[0.85rem] font-semibold disabled:cursor-wait disabled:opacity-60"
					>
						{running ? "Analyzing…" : "Analyze"}
					</button>
				</div>

				{error ? (
					<div
						className="tg-alert-error rounded-xl px-3 py-2 text-[0.85rem]"
						role="alert"
					>
						{error}
					</div>
				) : null}

				{result ? (
					<pre className="tg-code-block tg-scrollbar max-h-[360px] overflow-auto rounded-xl px-3 py-2 font-mono text-[0.8rem] leading-5">
						{result}
					</pre>
				) : null}
			</div>
		</section>
	);
};
